import { AssetManagerInvoice, LanguageNameSpaces } from "@shared/types";
import ExpandingCard from "common/Boxes/ExpandingCard.tsx";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import React from "react";
import useInvoice from "features/payouts/Invoices/hooks/useInvoice.tsx";
import Text from "common/Typography/Text.tsx";
import { formatDate } from "@shared/utils/date.ts";
import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import CardActions from "common/Lists/CardsList/CardActions.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { toFixed } from "@shared/utils/numbers.ts";
import { USD_PRECISION } from "features/affiliation/common/constants.ts";
import { COLUMN_SPANS } from "features/payouts/Invoices/constants.ts";
import styled, { css, useTheme } from "styled-components";
import { ChevronDown } from "lucide-react";
import CaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import useAccounts from "features/payouts/Invoices/hooks/useAccounts.ts";
import InvoiceActions from "features/payouts/Invoices/InvoiceActions.tsx";
import { formatUsd } from "features/affiliation/NewLook/newLookUtils";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface InvoiceCardProps {
  invoice: AssetManagerInvoice;
  toggleExpanded: (key: string | number) => void;
  isExpanded: boolean;
  /** New look: where the row sits in the list, which reads as one panel. */
  isFirst?: boolean;
  isLast?: boolean;
}

const InvoiceCard = ({
  invoice,
  toggleExpanded,
  isExpanded,
  isFirst,
  isLast,
}: InvoiceCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const { getAccountsNames, clientToExchangeMapping } = useAccounts();
  const { getStatusTag, getInvoiceActions } = useInvoice();
  const { isNewLook } = useTheme();

  const extraInfoData = [
    {
      name: t("invoices.table.payment_method"),
      value: invoice.payout_method_name,
    },
    {
      name: t("invoices.table.period"),
      value: (
        <Text>
          {formatDate(invoice.period_start, false, true)}
          <span style={{ margin: "0 4px" }}>-</span>
          {formatDate(invoice.period_end, false, true)}
        </Text>
      ),
    },
    {
      name: t("invoices.table.total_pnl"),
      value: isNewLook
        ? formatUsd(invoice.total_pnl)
        : `$${toFixed(invoice.total_pnl, USD_PRECISION)}`,
    },
  ];

  const collapsibleContent = (
    <>
      {extraInfoData?.map((item, index) => {
        return (
          <ExtraInfoItem
            name={item.name.toString()}
            value={item.value}
            key={index}
            bordered={false}
            height={36}
            valueWrapperStyle={{
              display: "flex",
              justifyContent: "flex-end",
            }}
            nameWrapperStyle={{
              whiteSpace: "nowrap",
              fontSize: typography.fontSizes.S,
            }}
          />
        );
      })}
      <CardActions
        borderTop={true}
        actionsData={getInvoiceActions(invoice).map(({ label, ...action }) => ({
          ...action,
          text: label,
        }))}
        size="m"
      />
    </>
  );

  if (isNewLook) {
    return (
      <NewLookCard
        $isFirst={isFirst}
        $isLast={isLast}
        collapsibleContent={
          <NewLookDetails>
            {extraInfoData.map((item) => (
              <NewLookDetail key={item.name}>
                <span>{item.name}</span>
                <span>{item.value}</span>
              </NewLookDetail>
            ))}
          </NewLookDetails>
        }
        expanded={isExpanded}
        setExpanded={() => toggleExpanded(invoice.id)}
      >
        <NewLookLine>
          <NewLookDate>
            {formatDate(invoice.created_at, true, true)}
          </NewLookDate>
          <NewLookEnd onClick={(e) => e.stopPropagation()}>
            {getStatusTag(invoice)}
            <InvoiceActions
              actions={getInvoiceActions(invoice)}
              largeTrigger={true}
            />
          </NewLookEnd>
        </NewLookLine>
        <NewLookLine>
          <ExchangesIconsGroup
            clients={getAccountsNames(invoice.client_ids)}
            clientToExchangeMapping={clientToExchangeMapping}
            size={24}
          />
          <NewLookAmount>
            {formatUsd(invoice.profit_share_amount)}
            <span>({invoice.profit_sharing_percent}%)</span>
            <NewLookChevron $expanded={isExpanded}>
              <ChevronDown size={16} strokeWidth={1.75} />
            </NewLookChevron>
          </NewLookAmount>
        </NewLookLine>
      </NewLookCard>
    );
  }

  return (
    <ExpandingCard
      collapsibleContent={collapsibleContent}
      expanded={isExpanded}
      setExpanded={() => toggleExpanded(invoice.id)}
      style={{ padding: 12 }}
    >
      <StatusWrapper>{getStatusTag(invoice)}</StatusWrapper>
      <Row gutter={12} style={{ alignItems: "center" }}>
        <Col span={COLUMN_SPANS[0]}>
          {formatDate(invoice.created_at, true, true)}
        </Col>
        <Col span={COLUMN_SPANS[1]}>
          <ExchangesIconsGroup
            clients={getAccountsNames(invoice.client_ids)}
            clientToExchangeMapping={clientToExchangeMapping}
            size={28}
          />
        </Col>
        <Col span={COLUMN_SPANS[2]}>
          <Text style={{ marginRight: 4 }}>
            ${toFixed(invoice.profit_share_amount, USD_PRECISION)}
          </Text>
          <Text type="secondary">({invoice.profit_sharing_percent}%)</Text>
        </Col>
        <Arrow $expanded={isExpanded} />
      </Row>
    </ExpandingCard>
  );
};
export default InvoiceCard;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: ${(p) => p.theme.borders.lightest};
  margin-bottom: 16px;
  padding-bottom: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      justify-content: flex-start;
      border-bottom: 0;
      margin-bottom: 10px;
      padding-bottom: 0;
    `}
`;
const Arrow = styled(CaretRightOutlined)<{ $expanded: boolean }>`
  position: absolute;
  right: 0;
  color: ${(p) => p.theme.colors.primaryGrey};
  ${(p) => p.$expanded && `transform: rotate(90deg);`}
`;

// The invoices read as one panel of rows: hairlines between them, the
// panel's radius on the first and last.
const NewLookCard = styled(ExpandingCard)<{
  $isFirst?: boolean;
  $isLast?: boolean;
}>`
  && {
    padding: 12px 16px;
    cursor: pointer;
    border: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    border-top-width: ${(p) => (p.$isFirst ? 1 : 0)}px;
    border-radius: ${(p) => {
      const top = p.$isFirst ? NEW_LOOK_RADIUS.panel : 0;
      const bottom = p.$isLast ? NEW_LOOK_RADIUS.panel : 0;
      return `${top}px ${top}px ${bottom}px ${bottom}px`;
    }};
    clip-path: ${(p) =>
      `inset(${p.$isFirst ? -24 : 0}px -24px ${p.$isLast ? -24 : 0}px -24px)`};
    box-shadow: ${(p) => (p.theme.isDarkMode ? "none" : p.theme.shadows.panel)};
    background: ${(p) => getNewLookTokens(p.theme).panel};
  }
  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`;
const NewLookLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  min-height: 26px;
`;
const NewLookDate = styled.div`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
const NewLookAmount = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  > span {
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
`;
const NewLookChevron = styled.i<{ $expanded: boolean }>`
  display: inline-flex;
  margin-left: 2px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  transition: transform 0.25s;
  ${(p) => p.$expanded && "transform: rotate(180deg);"}
`;
const NewLookEnd = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
`;
/* The card's divider has 16px above it and 12px (+4px here) below it. */
const NewLookDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
`;
const NewLookDetail = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  > span:first-child {
    flex: none;
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  > span:last-child {
    min-width: 0;
    text-align: right;
    color: ${(p) => getNewLookTokens(p.theme).text};
    font-variant-numeric: tabular-nums;
  }
`;
